import { Link } from "react-router-dom";
import { ArrowRight, Home, Key, Percent, Receipt, Calculator, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Layout from "@/components/Layout";

const dispositifsAccession = [
  {
    id: "brs",
    title: "Bail Réel Solidaire (BRS)",
    icon: Home,
    description: "Devenez propriétaire à prix réduit grâce à la dissociation du foncier et du bâti.",
    avantages: [
      "Prix d'achat réduit de 15% à 40%",
      "TVA à 5,5% au lieu de 20%",
      "Frais de notaire réduits",
      "Logement neuf ou réhabilité",
      "Revente encadrée pour maintenir l'accessibilité",
    ],
    conditions: [
      "Résidence principale obligatoire",
      "Plafonds de ressources (environ 80% des ménages éligibles)",
      "Signature avec un Organisme de Foncier Solidaire (OFS)",
      "Redevance mensuelle modérée pour le terrain",
    ],
    fonctionnement: "Vous achetez uniquement les murs du logement, pas le terrain. L'OFS reste propriétaire du foncier moyennant une redevance mensuelle modique (environ 1€/m²/mois).",
    exemple: "Pour un appartement de 250 000€ : prix BRS environ 175 000€ soit 75 000€ d'économie.",
  },
  {
    id: "psla",
    title: "Location-Accession (PSLA)",
    icon: Key,
    description: "Louez puis devenez propriétaire progressivement, sans apport initial.",
    avantages: [
      "Pas d'apport personnel nécessaire",
      "TVA à 5,5%",
      "Exonération de taxe foncière (15 ans)",
      "Prix plafonné et sécurisé",
      "Garanties de rachat et de relogement",
    ],
    conditions: [
      "Phase locative de 1 à 5 ans",
      "Plafonds de ressources PSLA",
      "Agrément préfectoral du programme",
      "Option d'achat à date fixée",
    ],
    fonctionnement: "Pendant la phase locative, vous versez une redevance composée d'un loyer et d'une part acquisitive. À la levée d'option, la part acquisitive vient en déduction du prix de vente.",
    exemple: "Redevance mensuelle de 800€ dont 200€ d'épargne. Après 3 ans : 7 200€ déduits du prix d'achat.",
  },
  {
    id: "ptz",
    title: "Prêt à Taux Zéro (PTZ)",
    icon: Percent,
    description: "Un prêt sans intérêts pour financer jusqu'à 40% de votre résidence principale.",
    avantages: [
      "Aucun intérêt à rembourser",
      "Différé de remboursement possible (5 à 15 ans)",
      "Jusqu'à 40% du coût de l'opération",
      "Durée jusqu'à 25 ans",
      "Cumulable avec d'autres prêts",
    ],
    conditions: [
      "Primo-accédant (pas propriétaire depuis 2 ans)",
      "Résidence principale",
      "Plafonds de ressources selon zone",
      "Logement neuf ou ancien avec travaux (25%)",
    ],
    zones: [
      { zone: "Zone A bis / A", quotite: "40%", plafond: "150 000€ à 300 000€" },
      { zone: "Zone B1", quotite: "40%", plafond: "135 000€ à 255 000€" },
      { zone: "Zone B2", quotite: "20%", plafond: "110 000€ à 230 000€" },
      { zone: "Zone C", quotite: "20%", plafond: "100 000€ à 200 000€" },
    ],
    exemple: "Achat de 200 000€ en zone B1 : PTZ de 80 000€ à 0% sur 20 ans = économie d'environ 40 000€ d'intérêts.",
  },
  {
    id: "tva5",
    title: "TVA à Taux Réduit (5,5%)",
    icon: Receipt,
    description: "Bénéficiez d'une TVA réduite pour l'achat de votre résidence principale en zone ANRU.",
    avantages: [
      "TVA à 5,5% au lieu de 20%",
      "Économie de 14,5% sur le prix TTC",
      "Applicable au neuf et à la rénovation",
      "Cumulable avec PTZ et autres aides",
    ],
    conditions: [
      "Zone ANRU ou QPV (quartiers prioritaires)",
      "Périmètre de 300m ou 500m selon les cas",
      "Résidence principale",
      "Plafonds de ressources PLS",
      "Engagement de conservation 10 ans",
    ],
    fonctionnement: "Dans les quartiers en rénovation urbaine, les programmes neufs bénéficient d'une TVA réduite pour favoriser la mixité sociale.",
    exemple: "Appartement à 250 000€ HT : TVA 20% = 50 000€ vs TVA 5,5% = 13 750€. Économie : 36 250€.",
  },
];

const faqAccession = [
  {
    question: "Puis-je cumuler plusieurs dispositifs d'accession aidée ?",
    answer: "Oui, dans la plupart des cas. Par exemple, vous pouvez acheter en BRS et bénéficier du PTZ. La TVA à 5,5% est également cumulable avec le PTZ. Le PSLA inclut déjà la TVA réduite dans son montage.",
  },
  {
    question: "Que se passe-t-il si je veux revendre un logement en BRS ?",
    answer: "La revente est encadrée : le prix de revente est plafonné pour maintenir l'accessibilité du logement. Vous ne réalisez pas de plus-value spéculative, mais vous récupérez votre investissement majoré de l'inflation.",
  },
  {
    question: "Comment savoir si je suis éligible au PTZ ?",
    answer: "L'éligibilité dépend de vos revenus N-2, de la composition du foyer et de la zone géographique. Notre simulateur peut vous donner une première estimation. Un courtier affinera ensuite votre dossier.",
  },
  {
    question: "La location-accession est-elle risquée ?",
    answer: "Non, c'est un dispositif très sécurisé. Vous bénéficiez d'une garantie de rachat si vous ne pouvez pas lever l'option, et d'une garantie de relogement. Vos versements ne sont jamais perdus.",
  },
  {
    question: "Comment trouver des programmes en BRS ou PSLA ?",
    answer: "Ces programmes sont proposés par des bailleurs sociaux et des promoteurs agréés. Inter'med Immobilier peut vous orienter vers les programmes disponibles à Nantes et en Loire-Atlantique.",
  },
];

const AccessionAidee = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-growth py-20" data-testid="accession-hero">
        <div className="container-custom">
          <div className="max-w-3xl text-white">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4 animate-fade-in-up">
              Accession Aidée à la Propriété
            </h1>
            <p className="text-lg text-white/80 animate-fade-in-up animation-delay-200">
              Découvrez les dispositifs permettant aux primo-accédants de devenir
              propriétaires avec un budget maîtrisé : BRS, Location-Accession, PTZ, TVA réduite.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-spacing bg-paper" data-testid="accession-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {dispositifsAccession.map((dispositif, index) => {
              const Icon = dispositif.icon;
              return (
                <Card
                  key={dispositif.id}
                  className="dispositif-card animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  id={dispositif.id}
                  data-testid={`accession-card-${dispositif.id}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-md bg-growth/10 text-growth">
                        <Icon size={28} />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-xl text-harbor">{dispositif.title}</CardTitle>
                        <p className="text-muted-foreground text-sm mt-1">{dispositif.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {/* Avantages */}
                    <div>
                      <h4 className="font-semibold text-sm text-harbor mb-3">Avantages</h4>
                      <ul className="space-y-2">
                        {dispositif.avantages.map((avantage, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-growth mt-2 flex-shrink-0" />
                            <span className="text-muted-foreground">{avantage}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Conditions */}
                    <div>
                      <h4 className="font-semibold text-sm text-harbor mb-3">Conditions</h4>
                      <ul className="space-y-2">
                        {dispositif.conditions.map((condition, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-clay mt-2 flex-shrink-0" />
                            <span className="text-muted-foreground">{condition}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Fonctionnement */}
                    <div className="p-4 bg-gray-50 rounded-md">
                      <h4 className="font-semibold text-sm text-harbor mb-2">Comment ça marche ?</h4>
                      <p className="text-sm text-muted-foreground">{dispositif.fonctionnement}</p>
                    </div>

                    {/* PTZ zones table */}
                    {dispositif.zones && (
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-sm text-harbor mb-3">Quotité et plafonds par zone</h4>
                        <div className="space-y-2">
                          {dispositif.zones.map((z, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                              <span className="font-medium">{z.zone}</span>
                              <span className="text-muted-foreground">{z.quotite}</span>
                              <span className="text-growth font-medium">{z.plafond}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Exemple */}
                    <div className="p-4 bg-growth/5 rounded-md border border-growth/20">
                      <h4 className="font-semibold text-sm text-growth mb-2">Exemple concret</h4>
                      <p className="text-sm text-muted-foreground">{dispositif.exemple}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white" data-testid="accession-faq">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <div className="mx-auto p-3 bg-harbor/10 rounded-full w-fit mb-4">
                <HelpCircle className="text-harbor" size={28} />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-harbor mb-2">
                Questions fréquentes
              </h2>
              <p className="text-muted-foreground">
                Tout ce que vous devez savoir sur l'accession aidée
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqAccession.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border rounded-md px-4"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-medium text-harbor hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-harbor" data-testid="accession-cta">
        <div className="container-custom text-center text-white">
          <h2 className="font-heading text-2xl md:text-3xl mb-4">
            Prêt à devenir propriétaire ?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Nos conseillers vous accompagnent pour trouver le dispositif le mieux adapté
            à votre situation et identifier les programmes disponibles.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/simulateur">
              <Button className="bg-clay hover:bg-clay/90 text-white" data-testid="accession-simuler">
                Simuler mon projet
                <Calculator className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10" data-testid="accession-contact">
                Prendre rendez-vous
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AccessionAidee;
